// Module 10 — selection PARCEL-BORDER highlight: a single pooled polyline that traces the perimeter ring
// of the currently-selected node's footprint parcel in the hot selection colour, so a click is obvious on
// the polygon BORDER (this) as well as the centre point (selection-marker.ts) — the user's "the selection
// must be clear and obvious by changing the colour of the polygon border AND the centre point". The ring
// geometry comes from the SAME parcels the wall/footprint layers draw (neighbourhood SceneSpec.walls +
// facility parcels), joined by node id, so every parcel kind — neighbourhood, substation, plant — gets the
// same border treatment regardless of which primitive drew it. The pooled polyline is created once then
// repositioned/shown/hidden in place (pool discipline, same as the flow dots + selection marker). Its pick
// id kind is "sel-marker", which resolvePick rejects, so the bright border never swallows the next click.
// Boundary: same-module + domain only.
import type { CesiumLike, PolylineCollectionLike, PolylineLike } from "./cesium-api";

export interface SelectionOutline {
  // sel → trace its parcel ring in the hot colour; undefined / no-parcel node → hide the border.
  set(sel: { kind: string; id: string } | undefined): void;
}

const RING_HEX = "#F0E442"; // hot yellow rim — matches the selection-marker ring's outline colour
const RING_WIDTH = 6; // px — reads as a bold border without burying the parcel fill

export function makeSelectionOutline(
  cesium: CesiumLike,
  lines: PolylineCollectionLike,
  // node id → its parcel ring (lon/lat vertices), or undefined when the node has no footprint parcel
  ringLookup: (id: string) => readonly { lon: number; lat: number }[] | undefined,
): SelectionOutline {
  let line: PolylineLike | null = null;
  let material: unknown = null;

  return {
    set(sel): void {
      const ring = sel && sel.kind === "node" ? ringLookup(sel.id) : undefined;
      if (!ring || ring.length < 2) {
        if (line) line.show = false; // no parcel (a junction / point bus) — the centre-point halo carries it
        return;
      }
      // flatten + close the ring so the border draws as one continuous loop
      const flat: number[] = [];
      for (const c of ring) flat.push(c.lon, c.lat);
      flat.push(ring[0]!.lon, ring[0]!.lat);
      const positions = cesium.Cartesian3.fromDegreesArray(flat);
      if (!material) {
        material = new cesium.Material({
          fabric: { type: "Color", uniforms: { color: cesium.Color.fromCssColorString(RING_HEX).withAlpha(0.95) } },
        });
      }
      if (!line) {
        line = lines.add({ positions, width: RING_WIDTH, material, id: { kind: "sel-marker", id: "outline" }, show: true });
      } else {
        line.positions = positions;
        line.show = true;
      }
    },
  };
}
